import { API_DOMAIN, PROTOCOL } from "@/constants/url";
import { tenant as currentTenant, getBaseURL } from "./tenant";
import { URL_INFO_REGEX } from "@/constants/regex";

/**
 * Function returning infos of a given potential link
 * @param {string} link 
 */
export function getLinkInfos(link) {
    const matchArray = link.match(URL_INFO_REGEX) ?? [];
    const valid = matchArray.length > 0;

    const { protocol, host, subdomains, domain, tld, port, path, params } = matchArray.groups ?? {};

    const url = `${protocol ?? 'http'}://${host}${port ? `:${port}` : ''}${path ?? ''}${params ?? ''}`;

    return {
        input: link,
        valid,
        protocol,
        host,
        subdomains: subdomains?.split('.').filter(Boolean) ?? [],
        domain,
        tld,
        port: port && parseInt(port),
        path,
        params: valid ? new URL(url).searchParams : undefined,
    }
};

/**
 * This interceptor transforms any param list field to a comma separated string field.
 * @param {Record<string, unknown>} config
 * @returns 
 */
export function listParamsInterceptor({ params, ...config }) {

    if (params) {
        const paramEntries = Object.entries(params)
            .map(([key, value]) => ([
                key,
                value instanceof Array ? value.join(',') : value,
            ]));
        params = Object.fromEntries(paramEntries);
    }

    return {
        ...config,
        params,
    };
}

export function getLocationHref() {
    return window.location.href;
}

export function getLocationURL() {
    return new URL(getLocationHref());
}

export function getLocationSearchParams() {
    const locationRawSearch = window.location.search;
    return new URLSearchParams(locationRawSearch);
}

/**
 * Retrieves the value(s) of a query parameter from the current URL.
 * Returns null if the parameter does not exist,
 * a single string if there is one occurrence,
 * or an array of strings if the parameter appears multiple times.
 *
 * @param {string} key - The query parameter name to retrieve.
 * @returns {string | string[] | null} The parameter value(s), or null if not found.
 *
 * @example
 * // Current URL: https://example.com/search?q=hello
 * getLocationSearchParam('q');     // => "hello"
 * getLocationSearchParam('missing'); // => null
 *
 * // Current URL: https://example.com/search?tag=a&tag=b
 * getLocationSearchParam('tag'); // => ["a", "b"]
 */
export function getLocationSearchParam(key) {
    const locationSearchParams = getLocationSearchParams();
    const hasParam = locationSearchParams.has(key);

    if (!hasParam)
        return null;

    const results = locationSearchParams.getAll(key);
    const areMultipleResults = results.length > 1;

    if (areMultipleResults)
        return results;

    return results[0];
}

/**
 * Updates a query parameter in the current URL without reloading the page.
 *
 * @param {string} key - The query parameter name to set.
 * @param {string} value - The value to assign to the query parameter.
 * @returns {URL} The updated URL object.
 *
 * @example
 * // Current URL: https://example.com/search?q=hello
 * setLocationSearchParam('q', 'world');
 * // New URL: https://example.com/search?q=world
 */
export function setLocationSearchParam(key, value) {
    const url = getLocationURL();
    url.searchParams.set(key, value);
    window.history.pushState(null, '', url.toString());
    return url;
}

/**
 * Removes a query parameter from the current URL without reloading the page.
 * Has no effect if the parameter does not exist.
 *
 * @param {string} key - The query parameter name to remove.
 * @returns {URL} The updated URL object.
 *
 * @example
 * // Current URL: https://example.com/search?q=hello&page=2
 * deleteLocationSearchParam('page');
 * // New URL: https://example.com/search?q=hello
 */
export function deleteLocationSearchParam(key) {
    const url = getLocationURL();
    url.searchParams.delete(key);
    window.history.pushState(null, '', url.toString());
    return url;
}

export function appendToURLSearchParams(url, name, value) {
    const urlCopy = new URL(url);
    urlCopy.searchParams.append(name, value);
    return urlCopy;
}

/**
 * @param {string[]} paramKeys 
 * @param {"history_push" | "history_replace" | "page_refresh"} action 
 */
export function removeLocationSearchParamsFromKeys(paramKeys, action = "history_push") {
    const locationUrl = getLocationURL();
    action = action.toLowerCase();

    const hasParamToRemove = paramKeys.some(paramKey => locationUrl.searchParams.has(paramKey));
    if (!hasParamToRemove)
        return false;

    for (const paramKey of paramKeys) {
        locationUrl.searchParams.delete(paramKey);
    }

    const cleanedUrl = locationUrl.toString();

    switch (action) {
        case "history_push":
            window.history.pushState({}, document.title, cleanedUrl);
            break;
        case "history_replace":
            window.history.replaceState({}, document.title, cleanedUrl);
            break;
        case "page_refresh":
            window.location.replace(cleanedUrl);
            break;
        default:
            console.warn(`${action} not supported.`);
            break;
    }
}

/**
 * Build a full URL from a relative path stored in DB
 * @param {string | null | undefined} path
 * @returns {string | null}
 */
export function buildMediaUrl(path) {

    if (typeof path !== "string" || path === "") {
        return null;
    }

    const baseUrl = getBaseURL();
    const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return normalizedBase + normalizedPath;
}


// WEBPACK FOOTER //
// ./src/helpers/url.js